// BenefitsSection.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { getKgInfo } from '../../../../cabinet/wizplain/activity';
import { loadFileAsBase64 } from '../../../../../services/apiClient';

import Step4 from '../../../../cabinet/wizplain/components/Step4Upd';
import { createAddressObj, formatAddress } from '../../../../cabinet/wizplain/helpers/address';

const translateStatus = (status)=>{

  const st_obj = {
    'invite-accepted': "Надає документи",
    'invite-sent': "Запрошення надіслане",
  }

  if(st_obj[status]) return st_obj[status];

  return status;
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const BENEFIT_ID_BROTHERS = '5bfb996f0b7fb2059b8828c9';
const BENEFIT_ID_EMPLOYEE = '5c16b5a22f8be751f04fce92';
const BENEFIT_ID_TERRITORIAL = '60e81561904ae40729056dc4';

const getStatusBenefits = (queueNumber, benefit) => {
  if (!benefit) return false;
  if (!queueNumber || queueNumber.length < 5) return false;
  const { isApproved, benefitId } = benefit;
  if (!isApproved || !benefitId) return false;
  const code = queueNumber.slice(0, 2);
  if (isNaN(+code) || +code >= 98) return false;
  return true;
};

const getKgInfo_ = async (benefit) => {
  const { brothers, personal, kindergartenId } = benefit;
  if (!brothers && !personal && !kindergartenId) return null;
  const id_kg = (brothers || personal)?.kg?._id || kindergartenId || null;
  if (!id_kg) return null;
  const info = await getKgInfo(id_kg);
  if (!info || !info[0]) return null;
  return { name: info[0].name, number: info[0].number };
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try { return new Date(dateString).toLocaleDateString('uk-UA'); } catch { return dateString; }
};

const getDocumentIcon = (type) => {
  const icons = { pdf: '📄', jpg: '🖼️', png: '🖼️', zip: '📦', docx: '📝', xlsx: '📊' };
  return icons[type] || '📎';
};

// ---------------------------------------------------------------------------
// Адаптер: mapped benefit → formData.benefits (формат Step4)
// ---------------------------------------------------------------------------
const benefitToFormData = (benefit) => ({
  type:                    benefit?.benefitId         || '',
  name:                    benefit?.benefitName        || '',
  documentType:            benefit?.documentType       || '',
  documentNumber:          benefit?.documentNumber     || '',
  issuedTo:                benefit?.documentTo         || '',
  issueDate:               benefit?.documentIssueDate  || '',
  // documents вже змаплені — передаємо щоб FileUploader показав поточні файли
  files:                   benefit?.documents          || [],
  address:                 benefit?.address            || createAddressObj(),
  kindergarten:            benefit?.kindergartenId     || '',
  siblingBirthCertificate: benefit?.brothers?.bcert    || '',
  siblingName:             benefit?.brothers?.name     || '',
  siblingBirthDate:        benefit?.brothers?.bdate    || '',
  employeeName:            benefit?.personal?.name     || '',
  employeeRnokpp:          benefit?.personal?.ipn      || '',
});

// ---------------------------------------------------------------------------
// Адаптер: formData.benefits → payload для PUT requests/:id/benefits
// ---------------------------------------------------------------------------
const formDataToBenefitPayload = (fd, existingId) => ({
  ...(existingId ? { _id: existingId } : {}),
  benefitId:         fd.type,
  benefitName:       fd.name,
  documentType:      fd.documentType,
  documentNumber:    fd.documentNumber,
  documentTo:        fd.issuedTo,
  documentIssueDate: fd.issueDate,
  benScan:           fd.files || [],
  address:           fd.address,
  kindergartenId:    fd.kindergarten,
  brothers: (fd.siblingName || fd.siblingBirthCertificate || fd.siblingBirthDate)
    ? { name: fd.siblingName, bcert: fd.siblingBirthCertificate, bdate: fd.siblingBirthDate }
    : undefined,
  personal: (fd.employeeName || fd.employeeRnokpp)
    ? { name: fd.employeeName, ipn: fd.employeeRnokpp }
    : undefined,
});

// ---------------------------------------------------------------------------
// BenefitsSection
// ---------------------------------------------------------------------------
const BenefitsSection = ({ applicationData, mode, applicationId, onChange,
  updateBenefits, saveBenefitsWithFiles, verifyBenefit, declineBenefit, downloadFile }) => {
  const [benefitComment,      setBenefitComment]      = useState(applicationData.benefitComment || '');
  const [loading,             setLoading]             = useState(false);
  const [saving,              setSaving]              = useState(false);
  const [editingBenefitIndex, setEditingBenefitIndex] = useState(null); // null | number | 'new'
  const [editFormData,        setEditFormData]        = useState(null);
  const [editErrors,          setEditErrors]          = useState({});
  const [showDeclineModal,    setShowDeclineModal]    = useState(false);
  const [declineDescription,  setDeclineDescription]  = useState('');
  const [declining,           setDeclining]           = useState(false);

  // Перераховується при кожному оновленні applicationData
  const isApproved = useMemo(
    () => getStatusBenefits(
      applicationData.queueNumber,
      applicationData.benefits?.[0] || null
    ),
    [applicationData]
  );

  // b.id може бути відсутнім якщо mapper повернув _id під іншим ключем
  const activeBenefits = useMemo(
    () => (applicationData.benefits || []).filter(b => b.benefitId || b.id || b._id).map(b => ({ ...b, id: b.id || b._id })),
    [applicationData.benefits]
  );
  const [kgInfo, setKgInfo] = useState(null);

  useEffect(() => {
    if (applicationData.benefits?.[0]) {
      getKgInfo_(applicationData.benefits[0]).then(setKgInfo);
    }
  }, [applicationData.benefits]);

  // --- відкрити форму редагування ---
  const openEdit = (index) => {
    const benefit = index === 'new'
      ? null
      : activeBenefits[index];
    setEditFormData(benefitToFormData(benefit));
    setEditErrors({});
    setEditingBenefitIndex(index);
  };

  const closeEdit = () => {
    setEditingBenefitIndex(null);
    setEditFormData(null);
  };

  // --- onChange для Step4 ---
  // Step4 викликає onChange('benefits.fieldName', value)
  const handleStep4Change = (path, value) => {
    
    setEditFormData(prev => {
      const updated = { ...prev };
      // відрізаємо префікс 'benefits.'
      const key = path.startsWith('benefits.') ? path.slice(9) : path;
      const parts = key.split('.');
      if (parts.length === 1) {
        updated[key] = value;
      } else {
        updated[parts[0]] = { ...(updated[parts[0]] || {}), [parts[1]]: value };
      }
      return updated;
    });
  };

  // --- зберегти ---
  const handleSave = async () => {
    try {
      setSaving(true);
      const existingBenefits = activeBenefits;
      const existingId = editingBenefitIndex !== 'new'
        ? existingBenefits[editingBenefitIndex]?.id
        : undefined;

      const payload = formDataToBenefitPayload(editFormData, existingId);

      const newBenefits = editingBenefitIndex === 'new'
        ? [
            ...existingBenefits.map(b => formDataToBenefitPayload(benefitToFormData(b), b.id)),
            payload,
          ]
        : existingBenefits.map((b, i) =>
            i === editingBenefitIndex
              ? payload
              : formDataToBenefitPayload(benefitToFormData(b), b.id)
          );

      // originalBenefits потрібні для diff файлів
      const originalBenefits = editingBenefitIndex !== 'new'
        ? [activeBenefits[editingBenefitIndex]]
        : [];
      await saveBenefitsWithFiles(newBenefits, originalBenefits, applicationData.dictionary);
      closeEdit();
      await onChange();
    } catch (err) {
      console.error('Error saving benefit:', err);
      alert('Помилка збереження пільги');
    } finally {
      setSaving(false);
    }
  };

  // --- підтвердити пільгу ---
  const handleVerifyBenefit = async (benefitId) => {
    try {
      setLoading(true);
      await verifyBenefit(benefitId, benefitComment);
      await onChange();
    } catch (err) {
      console.error('Error verifying benefit:', err);
    } finally {
      setLoading(false);
    }
  };

  // --- скасувати пільгу ---
  const handleDeclineBenefit = async () => {
    if (!declineDescription.trim()) return;
    try {
      setDeclining(true);
      await declineBenefit(applicationData, declineDescription.trim());
      setShowDeclineModal(false);
      setDeclineDescription('');
      await onChange();
    } catch (err) {
      console.error('Error declining benefit:', err);
      alert('Помилка скасування пільги');
    } finally {
      setDeclining(false);
    }
  };

  // --- завантажити файл ---
  const handleDownload = async (doc, part) => {
    try {
      await downloadFile(applicationData.id, doc._id, part, doc.originalName);
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  };

  // --- перегляд файлу через FileUploader ---
  const handleFileViewRequest = async (fileId) => {
    const url = `requests/${applicationData.id}/file/${fileId}/benefits`;
    try {
      return await loadFileAsBase64(url);
    } catch (err) {
      console.error('Помилка завантаження файлу:', err);
      throw err;
    }
  };

  // ---- view-блоки -----------------------------------------------------------

  const blockDescGeneral = (benefit) => (
    <div className="benefit-details">
      <div className="benefit-detail-row">
        <span className="detail-label">Тип документу:</span>
        <span className="detail-value">{benefit.documentType}</span>
      </div>
      <div className="benefit-detail-row">
        <span className="detail-label">Номер документу:</span>
        <span className="detail-value">{benefit.documentNumber}</span>
      </div>
      {benefit.documentIssueDate && (
        <div className="benefit-detail-row">
          <span className="detail-label">Дата видачі:</span>
          <span className="detail-value">{benefit.issueDateFormatted || formatDate(benefit.documentIssueDate)}</span>
        </div>
      )}
      {benefit.documentTo && (
        <div className="benefit-detail-row">
          <span className="detail-label">Документ на ім'я:</span>
          <span className="detail-value">{benefit.documentTo}</span>
        </div>
      )}
      <div className="benefit-detail-row">
        <span className="detail-label">Статус:</span>
        <span className="detail-value">
          {isApproved
            ? <span className="status-badge status-verified">Підтверджено</span>
            : <span className="status-badge status-pending">Очікує підтвердження</span>
          }
        </span>
      </div>
      {isApproved && <>
        <div className="benefit-detail-row">
          <span className="detail-label">Підтвердив:</span>
          <span>{benefit.approvedBy}</span>
        </div>
        <div className="benefit-detail-row">
          <span className="detail-label">Дата підтвердження:</span>
          <span>{benefit.approvedDate}</span>
        </div>
      </>}
    </div>
  );

  const blockDescBrothers = (benefit) => {
    if (!benefit.brothers) return null;
    const { name, bcert, bdate } = benefit.brothers;
    return (
      <div className="benefit-details">
        <div className="benefit-detail-row">
          <span className="detail-label">ЗДО:</span>
          <span className="detail-value">{kgInfo?.number} {kgInfo?.name}</span>
        </div>
        <div className="benefit-detail-row">
          <span className="detail-label">Ім'я дитини:</span>
          <span className="detail-value">{name}</span>
        </div>
        <div className="benefit-detail-row">
          <span className="detail-label">Свідоцтво:</span>
          <span className="detail-value">{bcert}</span>
        </div>
        <div className="benefit-detail-row">
          <span className="detail-label">Дата народження:</span>
          <span className="detail-value">{formatDate(bdate)}</span>
        </div>
      </div>
    );
  };

  const blockDescPersonal = (benefit) => {
    if (!benefit.personal) return null;
    const { name, prof, ipn } = benefit.personal;
    return (
      <div className="benefit-details">
        <div className="benefit-detail-row">
          <span className="detail-label">ЗДО:</span>
          <span className="detail-value">{kgInfo?.number} {kgInfo?.name}</span>
        </div>
        <div className="benefit-detail-row">
          <span className="detail-label">Ім'я працівника:</span>
          <span className="detail-value">{name}</span>
        </div>
        <div className="benefit-detail-row">
          <span className="detail-label">РНОКПП:</span>
          <span className="detail-value">{ipn}</span>
        </div>
        <div className="benefit-detail-row">
          <span className="detail-label">Посада:</span>
          <span className="detail-value">{prof}</span>
        </div>
      </div>
    );
  };

  const blockDescTerritorial = (benefit) => {
    if (![BENEFIT_ID_TERRITORIAL].includes(benefit.benefitId)) return null;

    const {kindergartenId, address } = benefit;

    return(<div className="benefit-details">

      <div className="benefit-detail-row">
          <span className="detail-label">ЗДО:</span>
          <span className="detail-value">{kgInfo?.number} {kgInfo?.name}</span>
      </div>


      <div className="benefit-detail-row">
          <span className="detail-label">Адреса:</span>
          <span className="detail-value">{formatAddress(address, applicationData.dictionary?.addresses)}</span>
      </div>

      <div className="benefit-detail-row">
        <span className="detail-label">Статус:</span>
        <span className="detail-value">
          {isApproved
            ? <span className="status-badge status-verified">Підтверджено</span>
            : <span className="status-badge status-pending">Очікує підтвердження</span>
          }
        </span>
      </div>

      {isApproved && <>
        <div className="benefit-detail-row">
          <span className="detail-label">Підтвердив:</span>
          <span>{benefit.approvedBy}</span>
        </div>
        <div className="benefit-detail-row">
          <span className="detail-label">Дата підтвердження:</span>
          <span>{benefit.approvedDate}</span>
        </div>
      </>}

    </div>)
  }


  // Редагування пільг заборонено якщо заявка має статус invite-sent або invite-accepted
  const currentStatusName = applicationData.currentStatus?.name ?? applicationData.currentStatus;
  const canEdit = mode === 'edit' &&
    !['invite-sent', 'invite-accepted'].includes(currentStatusName);

  const isHasBenDesc = activeBenefits.length > 0;

  // ---- render ---------------------------------------------------------------

  // Враппер для Step4 всередині модалки
  const renderEditForm = (title) => (
    <div style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: 16, background: '#f8f9fa', marginTop: 12 }}>
      {title && <h5 className="mb-3">{title}</h5>}
      <Step4
        formData={{
          benefits:    editFormData,
          dictionary:  applicationData.dictionary,
          _id:         applicationData.id,
          // Step4 також читає selectedSchools для деяких пільг
          selectedSchools: applicationData.selectedSchools || [],
        }}
        onChange={handleStep4Change}
        errors={editErrors}
        onFileViewRequest={handleFileViewRequest}
      />
      <div className="d-flex gap-2 mt-3 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Збереження...' : '💾 Зберегти'}
        </button>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={closeEdit}
          disabled={saving}
        >
          Скасувати
        </button>
      </div>
    </div>
  );

  return (
    <div className="benefits-section">
      <h3 className="section-title">Пільги</h3>

      {mode === 'edit' && !canEdit && (
        <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: '0.9em' }}>
          ⚠️ Редагування пільг недоступне — заявка має статус <strong>{currentStatusName}</strong>
        </div>
      )}

      <div className="data-section">

        {isHasBenDesc
          ? activeBenefits.map((benefit, index) => (
            <div key={benefit.id || index} className="data-row benefit-item">

              <div className="benefit-header d-flex align-items-center gap-2 flex-wrap">
                <span className="data-value benefit-name">
                  {benefit.benefitName}
                  {isApproved && (
                    <span className="status-badge status-verified ms-2">Підтверджено</span>
                  )}
                </span>
                {canEdit && editingBenefitIndex !== index  && (
                  <>
                    { !isApproved && <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => openEdit(index)}
                    >
                      ✏️ Редагувати
                    </button>}
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => setShowDeclineModal(true)}
                      disabled={declining}
                    >
                      Скасувати пільгу
                    </button>
                  </>
                )}
              </div>

              {/* View-блок */}
              {editingBenefitIndex !== index && (
                [BENEFIT_ID_BROTHERS].includes(benefit.benefitId)
                  ? blockDescBrothers(benefit)
                  : [BENEFIT_ID_EMPLOYEE].includes(benefit.benefitId)
                    ? blockDescPersonal(benefit)
                    : [BENEFIT_ID_TERRITORIAL].includes(benefit.benefitId) ? blockDescTerritorial(benefit): blockDescGeneral(benefit)
              )}

              {/* Форма редагування */}
              {editingBenefitIndex === index && editFormData && renderEditForm()}
            </div>
          ))
          : (
            <div className="data-row">
              <span className="data-value">Пільги відсутні</span>
            </div>
          )
        }

        {/* Додати нову пільгу — тільки якщо поточних немає */}
        {canEdit && !isHasBenDesc && editingBenefitIndex !== 'new' && (
          <button
            className="btn btn-outline-primary btn-sm mt-2"
            onClick={() => openEdit('new')}
          >
            + Додати пільгу
          </button>
        )}

        {editingBenefitIndex === 'new' && editFormData && renderEditForm('Нова пільга')}
      </div>

      {/* Документи для пільг (read-only список з кнопкою download) */}
      <div className="documents-section">
        <h4 className="documents-title">Документи для пільги</h4>
        <div className="documents-list">
          {applicationData.benefitDocuments?.map(doc => (
            <div key={doc._id} className="document-item">
              <div className="document-info">
                <span className="document-icon">{getDocumentIcon(doc.type)}</span>
                <div className="document-details">
                  <div className="document-name">{doc.name}</div>
                  <div className="document-meta">
                    <span className="document-type">{doc.type}</span>
                    <span className="document-size">{doc.size}</span>
                    <span className="document-date">{doc.uploadedAt}</span>
                  </div>
                </div>
              </div>
              <button
                className="btn-download"
                onClick={() => handleDownload(doc, 'benefits')}
                title="Завантажити документ"
                disabled={loading}
              >
                ⬇️
              </button>
            </div>
          ))}
          {(!applicationData.benefitDocuments || applicationData.benefitDocuments.length === 0) && (
            <div className="no-documents">Документи відсутні</div>
          )}
        </div>
      </div>

      {/* Коментар і підтвердження пільги */}
      {!isApproved && canEdit && (
        <div className="actions-section">
          <div className="comment-field">
            <label htmlFor="benefit-comment" className="comment-label">
              Коментар до пільги:
            </label>
            <textarea
              id="benefit-comment"
              className="form-control comment-textarea"
              value={benefitComment}
              onChange={(e) => setBenefitComment(e.target.value)}
              placeholder="Введіть коментар до пільги..."
              rows="3"
            />
          </div>
          {applicationData.benefitStatus !== 'verified' && (
            <div className="action-buttons">
              {activeBenefits.map((benefit, index) => (
                <button
                  key={benefit.id || index}
                  className="btn btn-success"
                  onClick={() => handleVerifyBenefit(benefit.id)}
                  disabled={benefit.approved || loading}
                >
                  {loading ? 'Обробка...' : 'Підтвердити пільгу'}
                </button>
              ))}
              <button
                className="btn btn-danger"
                onClick={() => setShowDeclineModal(true)}
                disabled={loading || declining}
              >
                Скасувати пільгу
              </button>
            </div>
          )}
        </div>
      )}

      {/* Модалка підтвердження скасування пільги */}
      {showDeclineModal && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1060 }}
          onClick={() => !declining && setShowDeclineModal(false)}
        >
          <div
            className="application-modal"
            style={{ maxWidth: 480 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 className="modal-title">Скасування пільги</h2>
              <button
                className="close-button"
                onClick={() => setShowDeclineModal(false)}
                disabled={declining}
              >
                ×
              </button>
            </div>
            <div className="modal-content p-3">
              <p className="mb-2">Вкажіть причину скасування пільги:</p>
              <textarea
                className="form-control"
                rows="4"
                value={declineDescription}
                onChange={(e) => setDeclineDescription(e.target.value)}
                placeholder="Опис причини скасування..."
                disabled={declining}
                autoFocus
              />
              {!declineDescription.trim() && (
                <div className="text-danger small mt-1">Поле обов'язкове</div>
              )}
            </div>
            <div className="modal-actions">
              <div className="action-buttons">
                <button
                  className="btn btn-danger"
                  onClick={handleDeclineBenefit}
                  disabled={declining || !declineDescription.trim()}
                >
                  {declining ? 'Скасування...' : 'Підтвердити скасування'}
                </button>
              </div>
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeclineModal(false)}
                disabled={declining}
              >
                Закрити
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BenefitsSection;